import { module } from 'modujs';

import { gsap } from 'gsap'

import { models, SUPPORT_WEBGL } from '../utils/environment'
import { shuffle } from '../utils/array'
import { gltfLoader, disposeRenderer } from '../utils/3d'

import { AmbientLight, Scene, OrthographicCamera, Clock, WebGLRenderer, PCFSoftShadowMap, SRGBColorSpace, AxesHelper, GridHelper, DoubleSide, Group, AnimationMixer, AnimationClip, Object3D, PlaneGeometry, MeshBasicMaterial, Mesh } from 'three'
// import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { clone as cloneWithSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';

const walkSpeed = 0.845 // in meters/s
const walkDistance = 17;

class Character {
    constructor(modelPath, x, callback, initialProgress = 0) {
        this.wrapper = new Object3D();

        this.wrapper.position.x = x;
        this.direction = Math.round(Math.random()) * 2 - 1
        this.wrapper.rotation.y = this.direction == 1 ? Math.PI : 0;

        this.load(modelPath).then(() => {
            if(this.destroyed) return

            this.setScene(modelPath);
            this.animate(initialProgress, callback);
        })
    }

    animate(progress, callback = () => {}) {
        this.tw = gsap.fromTo(this.wrapper.position, { z: walkDistance * this.direction }, { z: walkDistance * -this.direction, duration: walkDistance/walkSpeed, ease: 'none', onComplete: callback });
        this.tw.progress(progress)
    }

    setScene(src) {
        this.scene = cloneWithSkeleton(models[src].glb.scene);
        this.mixer = new AnimationMixer(this.scene)
        let animation = models[src].glb.animations.find(item => item.name == 'walking')
        if(animation) {
            this.clip = this.mixer.clipAction(animation);
            this.clip.play()
        }
        this.scene.scale.set(1,1,1)
        this.wrapper.add(this.scene);
    }

    load(src) {
        return new Promise((resolve, reject) => {
            if(!models[src]) {
                models[src] = {
                    glb: null,
                    usdz: null,
                    isLoaded: false,
                    onLoad: resolve
                }
            } else {
                if(!models[src].isLoaded) {
                    models[src].onLoad = resolve
                } else {
                    resolve()
                }
                return;
            }

            gltfLoader.load(src, gltf => {
                models[src].glb = gltf
                models[src].isLoaded = true

                if(gltf.animations.length) {
                    models[src].mixer = new AnimationMixer( gltf.scene );
                    gltf.scene.traverse(function(obj) { obj.frustumCulled = false; });

                    models[src].animations = []
                    for(let i = 0; i < gltf.animations.length; i++) {
                        let name = gltf.animations[i].name;
                        if(models[src].animations[gltf.animations[i].name]) name+=i
                        models[src].animations[name] = models[src].mixer.clipAction(gltf.animations[i]);
                    }
                }

                models[src].onLoad()
            })
        });
    }

    destroy() {
        this.destroyed = true

        this.tw?.kill?.()
    }
}

export default class extends module {
    constructor(m) {
        super(m);

        this.modelsQty = 12;
        this.rowsQty = Math.max(this.modelsQty+1, 20)

        this.rowsUsed = [];
        this.modelsUsed = [];

        this.canvas = this.el;

        this.models = shuffle(this.getData('models').split(','));
        // this.models.splice(this.modelsQty, Infinity)

        this.characters = [];

        this.resize();
        this.resizeBind = this.resize.bind(this);
        window.addEventListener('resize', this.resizeBind);
    }

    init() {
        if(!SUPPORT_WEBGL) return;

        this.initRenderer();
        this.initScene();

        this.render();
    }

    initRenderer() {
        this.renderer = new WebGLRenderer({
            canvas: this.canvas,
            alpha: true,
            antialias: true,
            preserveDrawingBuffer: true // for screenshots
        })
        this.renderer.shadowMap.enabled = true
        this.renderer.shadowMap.type = PCFSoftShadowMap

        this.renderer.outputEncoding = SRGBColorSpace;
        // renderer.toneMapping = ACESFilmicToneMapping;
        // renderer.toneMappingExposure = 1;

        this.renderer.setSize(this.sizes.width, this.sizes.height)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

        this.clock = new Clock()
        this.previousTime = 0
    }

    initScene() {
        this.scene = new Scene();

        /**
         * Lights
         */
        const ambientLight = new AmbientLight(0xffffff, 6)
        this.scene.add(ambientLight)

        // Base camera
        this.aspect = this.sizes.width / this.sizes.height;
        this.setFrustumSize();
        this.camera = new OrthographicCamera(
            this.frustumSize * this.aspect / - 2,
            this.frustumSize * this.aspect / 2,
            this.frustumSize / 2, this.frustumSize / - 2,
            0,
            40
        );
        // this.camera = new PerspectiveCamera(30, this.sizes.width / this.sizes.height, 0.01,100)
        this.camera.position.set(10,7,10)
        this.scene.add(this.camera)

        // Controls
        this.controls = new OrbitControls(this.camera, this.canvas)
        this.controls.target.set(0, 0, 0)
        this.controls.enableDamping = true
        this.controls.enablePan = false
        this.controls.maxPolarAngle = this.controls.minPolarAngle = this.controls.getPolarAngle()
        // this.controls.autoRotate = true;
        // this.controls.autoRotateSpeed = 0.2;

        // Wrapper
        this.wrapper = new Group();
        this.wrapper.position.set(0,0,0);
        this.scene.add(this.wrapper)

        // const axesHelper = new AxesHelper( 1 );
        // this.wrapper.add( axesHelper );

        const gridHelper = new GridHelper( 100, 100, 0xcccccc, 0xcccccc );
        this.wrapper.add( gridHelper );

        // AREA FOR DEBUG
        // const geometry = new PlaneGeometry(this.rowsQty, walkDistance * 2);
        // const material = new MeshBasicMaterial( {color: 0x00ff00} );
        // const area = new Mesh( geometry, material );
        // area.position.y = -0.01;
        // area.rotation.x = -Math.PI/2
        // this.wrapper.add( area );

        for(let i = 0; i < this.modelsQty; i++) {
            this.spawn(Math.random())
        }
    }

    setFrustumSize() {
        this.frustumSize = this.aspect > 16/9 ? (1 - (this.aspect-16/9) * 0.2) * 10 : 10;
    }

    pickRow() {
        let row;
        while(!row || this.rowsUsed.includes(row)) {
            row = Math.floor(Math.random() * this.rowsQty - this.rowsQty/2)
        }
        this.rowsUsed.push(row);
        return row
    }

    pickModel() {
        let model;
        while(!model || this.modelsUsed.includes(model)) {
            model = this.models[Math.floor(Math.random() * this.models.length)]
        }
        this.modelsUsed.push(model)
        return model
    }

    spawn(progress = 0) {
        // Pick a row and model
        const row = this.pickRow()
        const model = this.pickModel()
        // console.log(row, model);

        const instance = new Character(model, row, () => {
            instance.destroy() // Make sure instance is properly discarded
            this.wrapper.remove(instance.wrapper); // Remove from world
            this.characters.splice(this.characters.indexOf(instance), 1) // Remove from characters list
            this.rowsUsed.splice(this.rowsUsed.indexOf(row), 1) // Release used row
            this.modelsUsed.splice(this.modelsUsed.indexOf(model), 1) // Release used model

            this.spawn(); // Spawn a new one instead
        }, progress);
        this.wrapper.add(instance.wrapper);
        this.characters.push(instance);
    }

    resize() {
        this.BCR = this.el.getBoundingClientRect()

        this.sizes = {
            width: this.BCR.width,
            height: this.BCR.height
        }

        this.renderer && this.renderer.setSize(this.sizes.width, this.sizes.height);

        if(this.camera) {
            // Update camera
            this.aspect = this.sizes.width / this.sizes.height;
            this.setFrustumSize();
            this.camera.left = this.frustumSize * this.aspect / - 2;
            this.camera.right = this.frustumSize * this.aspect / 2;
            this.camera.top = this.frustumSize / 2;
            this.camera.bottom = this.frustumSize / - 2;
            this.camera.updateProjectionMatrix()
        }

        // Update renderer
        if(this.renderer) {
            this.renderer.setSize(this.sizes.width, this.sizes.height)
            this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        }
    }

    render() {
        const elapsedTime = this.clock.getElapsedTime()
        this.deltaTime = elapsedTime - this.previousTime
        this.previousTime = elapsedTime

        // Update controls
        this.controls.update()

        // Render
        this.renderer.render(this.scene, this.camera)

        for(let character of this.characters) {
            character.mixer?.setTime?.(elapsedTime);
        }

        // Autorotate
        this.wrapper.rotation.set(0,this.wrapper.rotation.y + (0.001 * this.deltaTime/(1/60)),0)

        // Call tick again on the next frame
        this.raf = window.requestAnimationFrame(this.render.bind(this))
    }

    destroy() {
        for(let character of this.characters) {
            character.destroy?.();
        }

        window.removeEventListener('resize', this.resizeBind);
        window.cancelAnimationFrame(this.raf);

        disposeRenderer(this.renderer, this.scene, { controls: this.controls });

        this.scene = null;
        this.renderer = null;
        this.controls = null;
    }
}
